import React from 'react';
import { Link } from 'react-router-dom';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { LOGOS } from '../../constants/logos';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { APP_LINKS } from '../../constants/appLinks';
import appStoreBadgeEn from '../../assets/app/app-store-badge-en.svg';
import appStoreBadgeTr from '../../assets/app/app-store-badge-tr.svg';
import googlePlayBadgeEn from '../../assets/app/google-play-badge-en.png';
import googlePlayBadgeTr from '../../assets/app/google-play-badge-tr.png';

const Footer = () => {
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();
  const isTr = language === 'tr';

  const footerLinks = {
    organization: [
      { label: isTr ? 'TDT Hakkında' : 'About OTS', path: '/about' },
      { label: isTr ? 'Üye Devletler' : 'Member States', path: '/member-states' },
      { label: isTr ? 'Haberler ve Etkinlikler' : 'News & Events', path: '/news' },
      { label: isTr ? 'İletişim' : 'Contact', path: '/contact' },
    ],
    resources: [
      { label: isTr ? 'Belgeler' : 'Documents', path: '#' },
      { label: isTr ? 'Yayınlar' : 'Publications', path: '#' },
      { label: isTr ? 'Medya Merkezi' : 'Media Center', path: '#' },
      { label: isTr ? 'İstatistikler' : 'Statistics', path: '#' },
    ],
    connect: [
      { label: isTr ? 'Bize Ulaşın' : 'Contact Us', path: '/contact' },
      { label: isTr ? 'Kariyer' : 'Careers', path: '#' },
      { label: isTr ? 'Basın' : 'Press', path: '#' },
      { label: isTr ? 'Ortaklar' : 'Partners', path: '#' },
    ],
  };

  const memberFlags = [
    { name: 'Türkiye', flag: FLAGS.turkey },
    { name: 'Azerbaijan', flag: FLAGS.azerbaijan },
    { name: 'Kazakhstan', flag: FLAGS.kazakhstan },
    { name: 'Kyrgyzstan', flag: FLAGS.kyrgyzstan },
    { name: 'Uzbekistan', flag: FLAGS.uzbekistan },
  ];

  return (
    <footer style={{
      ...styles.footer,
      backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.primary,
    }}>
      <div style={styles.container}>
        <div style={styles.top}>
          <div style={styles.brand}>
            <Link to="/" style={styles.logo}>
              <img src={LOGOS.light} alt="Turkic Silk Road" style={styles.logoImage} />
              <span style={{
                ...styles.logoText,
                color: isDarkMode ? DARK_THEME.primary : COLORS.white,
              }}>Turkic Silk Road</span>
            </Link>
            <p style={{
              ...styles.brandDescription,
              color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.85)',
            }}>
              {isTr
                ? 'Türk devletlerini ortak miras, ekonomik iş birliği ve kültürel birlik yoluyla birbirine bağlıyoruz.'
                : 'Connecting Turkic nations through shared heritage, economic cooperation, and cultural unity.'}
            </p>
            <div style={styles.flags}>
              {memberFlags.map((item, index) => (
                <img 
                  key={index} 
                  src={item.flag} 
                  alt={item.name} 
                  style={styles.flagImage}
                  title={item.name}
                />
              ))}
            </div>
          </div>

          <div style={styles.links}>
            <div style={styles.linkColumn}>
              <h4 style={styles.linkTitle}>{isTr ? 'Kurumsal' : 'Organization'}</h4>
              {footerLinks.organization.map((link, index) => (
                <Link key={index} to={link.path} style={{
                  ...styles.link,
                  color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.75)',
                }}>
                  {link.label}
                </Link>
              ))}
            </div>

            <div style={styles.linkColumn}>
              <h4 style={styles.linkTitle}>{isTr ? 'Kaynaklar' : 'Resources'}</h4>
              {footerLinks.resources.map((link, index) => (
                <Link key={index} to={link.path} style={{
                  ...styles.link,
                  color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.75)',
                }}>
                  {link.label}
                </Link>
              ))}
            </div>

            <div style={styles.linkColumn}>
              <h4 style={styles.linkTitle}>{isTr ? 'Bağlantılar' : 'Connect'}</h4>
              {footerLinks.connect.map((link, index) => (
                <Link key={index} to={link.path} style={{
                  ...styles.link,
                  color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.75)',
                }}>
                  {link.label}
                </Link>
              ))}
            </div>

            <div style={styles.linkColumn}>
              <h4 style={styles.linkTitle}>{isTr ? 'Mobil Uygulama' : 'Get the App'}</h4>
              <a
                href={APP_LINKS.appStore}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="App Store"
              >
                <img
                  src={isTr ? appStoreBadgeTr : appStoreBadgeEn}
                  alt={isTr ? "App Store'dan İndirin" : 'Download on the App Store'}
                  style={styles.storeBadge}
                />
              </a>
              <a
                href={APP_LINKS.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Google Play"
              >
                <img
                  src={isTr ? googlePlayBadgeTr : googlePlayBadgeEn}
                  alt={isTr ? "Google Play'den alın" : 'Get it on Google Play'}
                  style={styles.storeBadgeGoogle}
                />
              </a>
            </div>
          </div>
        </div>

        <div style={{
          ...styles.divider,
          backgroundColor: isDarkMode ? DARK_THEME.border : 'rgba(255,255,255,0.2)',
        }}></div>

        <div style={styles.bottom}>
          <p style={{
            ...styles.copyright,
            color: isDarkMode ? DARK_THEME.textMuted : 'rgba(255,255,255,0.7)',
          }}>
            ©{new Date().getFullYear()} {isTr
              ? 'Türk Devletleri Teşkilatı. Tüm hakları saklıdır.'
              : 'Organization of Turkic States. All rights reserved.'}
          </p>
          <div style={styles.social}>
            <a
              href="https://www.instagram.com/organizationofturkicstates/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...styles.socialLink,
                color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.75)',
              }}
            >Instagram</a>
            <a
              href="https://twitter.com/Turkic_States"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...styles.socialLink,
                color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.75)',
              }}
            >Twitter</a>
            <a
              href="https://www.facebook.com/turkicstates/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...styles.socialLink,
                color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.75)',
              }}
            >Facebook</a>
            <a
              href="https://www.youtube.com/organizationofturkicstates"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...styles.socialLink,
                color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.75)',
              }}
            >YouTube</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const styles = {
  footer: {
    padding: '80px 20px 30px',
    color: COLORS.white,
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  top: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '50px',
    marginBottom: '50px',
  },
  brand: {
    flex: '1 1 300px',
    maxWidth: '350px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    textDecoration: 'none',
  },
  logoImage: {
    height: '45px',
    width: 'auto',
    objectFit: 'contain',
  },
  logoText: {
    fontSize: '20px',
    fontWeight: '700',
  },
  brandDescription: {
    fontSize: '15px',
    lineHeight: '1.7',
    marginBottom: '20px',
  },
  flags: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  flagImage: {
    height: '24px',
    width: 'auto',
    borderRadius: '3px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  links: {
    display: 'flex',
    gap: '60px',
    flexWrap: 'wrap',
  },
  linkColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  linkTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: COLORS.white,
    marginBottom: '10px',
  },
  link: {
    fontSize: '14px',
    transition: 'color 0.3s ease',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  divider: {
    height: '1px',
    marginBottom: '30px',
  },
  bottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '20px',
  },
  copyright: {
    fontSize: '14px',
  },
  social: {
    display: 'flex',
    gap: '25px',
  },
  socialLink: {
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  storeBadge: {
    height: '40px',
    display: 'block',
  },
  // Google Play rozetinin PNG'sinde kenar boşluğu gömülü olduğundan
  // App Store rozetiyle aynı görünmesi için daha yüksek tutulur
  storeBadgeGoogle: {
    height: '60px',
    display: 'block',
    margin: '-10px 0',
  },
};

export default Footer;
